/**
 * Chain Repository
 * Handles all chain-related API calls
 */

import { apiClient } from '../../client'
import type { PaginatedResponse } from '../role.repository'

export interface Chain {
    id: number
    tenant_id: number
    en_name: string
    ar_name?: string | null
    status: number
    created_by?: number | null
    updated_by?: number | null
    created_at: Date
    updated_at: Date
}

export interface CreateChainRequest {
    tenant_id: number
    en_name: string
    ar_name?: string
    status?: number
    // Note: created_by is automatically set by backend from logged-in user
}

export interface UpdateChainRequest {
    tenant_id?: number
    en_name?: string
    ar_name?: string
    status?: number
    // Note: updated_by is automatically set by backend from logged-in user
}

class ChainRepository {
    /**
     * Get all chains with pagination
     */
    async getAll(page: number = 1, limit: number = 10, filters?: any): Promise<PaginatedResponse<Chain>> {
        const params = new URLSearchParams({
            page: page.toString(),
            limit: limit.toString(),
        })

        // Add filters if provided
        if (filters) {
            if (filters.search) {
                params.append('search', filters.search)
            }
            if (filters.status) {
                params.append('status', filters.status)
            }
            if (filters.chain_id) {
                params.append('chain_id', filters.chain_id)
            }
        }

        return apiClient.get<PaginatedResponse<Chain>>(`/chains?${params.toString()}`)
    }

    /**
     * Get chain by ID
     */
    async getById(id: number): Promise<Chain> {
        return apiClient.get<Chain>(`/chains/${id}`)
    }

    /**
     * Create a new chain
     */
    async create(data: CreateChainRequest): Promise<Chain> {
        return apiClient.post<Chain>('/chains', data)
    }

    /**
     * Update an existing chain
     */
    async update(id: number, data: UpdateChainRequest): Promise<Chain> {
        return apiClient.put<Chain>(`/chains/${id}`, data)
    }

    /**
     * Delete a chain
     */
    async delete(id: number): Promise<{ message: string }> {
        return apiClient.delete<{ message: string }>(`/chains/${id}`)
    }

    /**
     * Export chains to Excel
     */
    async exportToExcel(filters?: any): Promise<Blob> {
        const params = new URLSearchParams()
        if (filters?.search) {
            params.append('search', filters.search)
        }
        if (filters?.status) {
            params.append('status', filters.status)
        }

        if (filters?.chain_id) {
            params.append('chain_id', filters.chain_id.toString())
        }
        const baseUrl = `/chains/export/excel`;
        const queryString = params.toString();
        const url = queryString ? `${baseUrl}?${queryString}` : baseUrl;


        return apiClient.getBlob(url, true, { showSuccessToast: true, successMessage: 'Exported Successfully' })
    }
}

// Export singleton instance
export const chainRepository = new ChainRepository()

